import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const NotificationPermissionModal = ({ user, onClose, forceShow = false }) => {
  const [showModal, setShowModal] = useState(forceShow);
  const [requesting, setRequesting] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('checking');

  useEffect(() => {
    // Si forceShow es true, mostrar inmediatamente
    if (forceShow) {
      console.log('🔔 Modal forzado por admin - mostrando inmediatamente');
      setShowModal(true);
      // Verificar estado de permisos para mostrar mensaje apropiado
      if ('Notification' in window) {
        setPermissionStatus(Notification.permission);
      }
      return;
    }

    const checkNotificationPermission = async () => {
      // Don't show if user is not logged in
      if (!user || !user.id) {
        setShowModal(false);
        return;
      }

      // ⚠️ Bloquear modal en sesión de soporte (clave maestra) — no debemos
      // pedirle a un admin que registre el dispositivo con datos del estudiante.
      if (localStorage.getItem('is_support_session') === 'true') {
        setShowModal(false);
        return;
      }

      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('Notifications not supported');
        setShowModal(false);
        return;
      }

      // Check current permission
      const permission = Notification.permission;
      setPermissionStatus(permission);
      
      // If permission is "default" (not asked yet), show modal
      if (permission === 'default') {
        setShowModal(true);
        return;
      }

      // If permission is "denied", don't show modal (user blocked notifications)
      if (permission === 'denied') {
        setShowModal(false);
        return;
      }

      // Permission is "granted" - check if subscribed to OneSignal
      if (permission === 'granted' && window.OneSignal) {
        try {
          const isSubscribed = await window.OneSignal.User.PushSubscription.optedIn;
          console.log('🔔 OneSignal subscription status:', isSubscribed);
          
          if (!isSubscribed) {
            // User has browser permission but not subscribed to OneSignal
            // Check if we already asked recently
            const askedBefore = localStorage.getItem(`notification_resub_${user.id}`);
            if (askedBefore) {
              const askedTime = parseInt(askedBefore);
              if (askedTime > Date.now()) {
                setShowModal(false);
                return;
              }
            }
            setShowModal(true);
          }
        } catch (e) {
          console.log('Error checking OneSignal subscription:', e);
        }
      }
    };

    // Verificar inmediatamente si OneSignal ya está cargado, sino esperar un poco
    if (window.OneSignal) {
      checkNotificationPermission();
    } else {
      const timer = setTimeout(checkNotificationPermission, 500);
      return () => clearTimeout(timer);
    }
  }, [user, forceShow]);

  const handleActivate = async () => {
    try {
      setRequesting(true);
      
      console.log('🔔 Requesting notification permission...');
      
      const permission = Notification.permission;

      // If permission not granted yet, request it
      if (permission === 'default') {
        const newPermission = await Notification.requestPermission();
        console.log('Browser permission result:', newPermission);
        
        if (newPermission !== 'granted') {
          console.log('Permission not granted');
          setShowModal(false);
          if (onClose) onClose();
          return;
        }
      }

      // Now try to subscribe to OneSignal
      if (window.OneSignal) {
        try {
          console.log('📱 Iniciando suscripción a OneSignal...');
          
          // Check if already opted in
          const currentOptedIn = window.OneSignal.User?.PushSubscription?.optedIn;

          // STEP 1 — login(user.id) PRIMERO para asociar external_id
          // antes de crear la push subscription. Esto garantiza que OneSignal
          // cree el registro de usuario bajo NUESTRO external_id desde el
          // principio, en lugar de un usuario anónimo que después hay que linkear.
          if (user && user.id) {
            try {
              await window.OneSignal.login(user.id);
              console.log('✅ OneSignal.login(user.id) completado:', user.id);
            } catch (loginErr) {
              console.warn('⚠️ login error (continuando):', loginErr?.message || loginErr);
            }
          }

          // STEP 2 — optIn si aún no está suscripto (sin opt-out previo destructivo)
          if (!currentOptedIn) {
            console.log('🔔 OptIn directo...');
            try {
              await window.OneSignal.User.PushSubscription.optIn();
              console.log('✅ OneSignal optIn completado');
            } catch (optInErr) {
              console.warn('⚠️ optIn error:', optInErr?.message || optInErr);
            }
          } else {
            console.log('✅ Ya está opted-in en este dispositivo.');
          }
          // STEP 3 — Esperar a que el subscription_id esté listo (hasta 5 segundos)
          let subscriptionId = window.OneSignal.User?.PushSubscription?.id;
          if (!subscriptionId) {
            for (let i = 0; i < 10; i++) {
              await new Promise(resolve => setTimeout(resolve, 500));
              subscriptionId = window.OneSignal.User?.PushSubscription?.id;
              if (subscriptionId) break;
            }
          }
          console.log('📱 Subscription ID:', subscriptionId ? subscriptionId.substring(0, 20) + '...' : 'NO obtenido');

          // STEP 4 — Tags + email (ESPERAR, no fire-and-forget) así el registro
          // OneSignal queda completo antes de cerrar el modal.
          if (user) {
            const tags = {
              email: user.email,
              user_id: user.id,
              rol: user.rol || '',
              nombre: user.nombre || '',
              apellido: user.apellido || ''
            };
            try {
              await window.OneSignal.User.addTags(tags);
              console.log('✅ Tags agregados a OneSignal');
            } catch (tagErr) {
              console.warn('⚠️ addTags error:', tagErr?.message || tagErr);
            }
            if (user.email) {
              try {
                await window.OneSignal.User.addEmail(user.email);
                console.log('✅ Email agregado a OneSignal:', user.email);
              } catch (emailErr) {
                console.warn('⚠️ addEmail error:', emailErr?.message || emailErr);
              }
            }
          }

          // STEP 5 — Persistir subscription_id en backend (el backend también
          // hará un upsert server-side a OneSignal API como respaldo por si
          // alguna llamada del SDK falló silenciosamente).
          if (subscriptionId) {
            const token = localStorage.getItem('token');
            if (token) {
              const API = process.env.REACT_APP_BACKEND_URL || '';
              try {
                const resp = await fetch(`${API}/api/usuarios/update-onesignal-subscription`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ subscription_id: subscriptionId })
                });
                if (resp.ok) {
                  console.log('✅ Subscription guardado en backend (upsert a OneSignal + Courier)');
                } else {
                  console.error('❌ Error guardando en backend:', resp.status);
                }
              } catch (e) {
                console.error('❌ Error de red:', e);
              }
            }
          } else {
            // Fallback: aunque no haya subscription_id, pegarle al backend
            // para que al menos sincronice external_id en OneSignal server-side.
            const token = localStorage.getItem('token');
            if (token) {
              const API = process.env.REACT_APP_BACKEND_URL || '';
              try {
                await fetch(`${API}/api/usuarios/ensure-onesignal-user`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                });
                console.log('✅ Ensure OneSignal user (sin sub_id aún)');
              } catch (e) {
                console.error('ensure-onesignal-user error:', e);
              }
            }
          }
          
        } catch (osError) {
          console.error('❌ OneSignal subscription error:', osError.message || osError);
        }
      }
      
      // Mark as shown
      if (user && user.id) {
        localStorage.setItem(`notification_asked_${user.id}`, 'true');
        localStorage.setItem(`notification_resub_${user.id}`, (Date.now() + 7 * 24 * 60 * 60 * 1000).toString());
      }
      
      // Close modal immediately - don't wait for background tasks
      setShowModal(false);
      if (onClose) onClose();
      
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setShowModal(false);
      if (onClose) onClose();
    } finally {
      setRequesting(false);
    }
  };

  const handleLater = () => {
    // Mark as asked but allow showing again in 7 days
    if (user && user.id) {
      const oneWeekFromNow = Date.now() + (7 * 24 * 60 * 60 * 1000);
      localStorage.setItem(`notification_asked_${user.id}`, oneWeekFromNow.toString());
    }
    
    setShowModal(false);
    if (onClose) onClose();
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
            <i className="fas fa-bell text-white text-3xl"></i>
          </div>
          <CardTitle className="text-2xl text-gray-900">
            📱 Mantente Informado
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-gray-700 text-lg">
            Activa las notificaciones para recibir alertas importantes directamente en tu dispositivo
          </p>

          {/* Benefits */}
          <div className="space-y-3 bg-blue-50 p-4 rounded-lg">
            <p className="font-semibold text-blue-900 mb-2">¿Qué recibirás?</p>
            
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                <i className="fas fa-tasks text-blue-700 text-sm"></i>
              </div>
              <div>
                <p className="text-blue-800 font-medium">Nuevas Tareas</p>
                <p className="text-blue-600 text-sm">Te avisaremos cuando se asigne una tarea</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                <i className="fas fa-calendar-alt text-green-700 text-sm"></i>
              </div>
              <div>
                <p className="text-blue-800 font-medium">Eventos y Clases</p>
                <p className="text-blue-600 text-sm">Recordatorios de eventos próximos</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center">
                <i className="fas fa-newspaper text-purple-700 text-sm"></i>
              </div>
              <div>
                <p className="text-blue-800 font-medium">Noticias Importantes</p>
                <p className="text-blue-600 text-sm">Actualizaciones del instituto</p>
              </div>
            </div>
          </div>

          {/* Privacy note */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <i className="fas fa-shield-alt text-gray-600"></i>
              <p className="text-sm text-gray-600">
                <strong>Tu privacidad es importante.</strong> Solo recibirás notificaciones relevantes y puedes desactivarlas en cualquier momento.
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 pt-2">
            <Button
              onClick={handleActivate}
              disabled={requesting}
              className="w-full instituto-gradient text-white font-medium py-3 text-lg"
            >
              {requesting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Activando...
                </>
              ) : (
                <>
                  <i className="fas fa-bell mr-2"></i>
                  Activar Notificaciones
                </>
              )}
            </Button>
            
            <Button
              onClick={handleLater}
              variant="outline"
              disabled={requesting}
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Más Tarde
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPermissionModal;
